import React from 'react';
import { Select, MenuItem, Box } from '@mui/material';
import selectIcon from '../../assets/images/select.png';

const CustomSelect = ({
  value,
  onChange,
  options = [],
  size = "small",
  width,
  height = "48px",
  backgroundColor = "white",
  borderRadius = "8px",
  borderColor = "#E9ECEF",
  hoverBorderColor = "#ADB5BD",
  focusBorderColor = "#FF43AA",
  fontSize = "15px",
  fontWeight = 500,
  padding = "12px 16px",
  color = "#495057",
  open,
  onOpen,
  onClose,
  name,
  required = false,
  fullWidth = false,
  disabled = false,
  menuMaxHeight,
  sx = {},
  ...props
}) => {
  const resolvedMenuMaxHeight = menuMaxHeight ?? 360;
  const defaultSx = {
    width: fullWidth ? '100%' : width,
    height,
    backgroundColor,
    borderRadius,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor,
      borderRadius
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: hoverBorderColor
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: focusBorderColor
    },
    '& .MuiSelect-select': {
      fontSize,
      color,
      fontWeight,
      padding
    },
    '& .MuiMenu-paper': {
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: '#FFF0F6 !important',
        '&:hover': {
          backgroundColor: '#FFE3EF !important'
        }
      }
    },
    ...sx
  };

  const menuItemSx = {
    fontSize,
    color,
    padding,
    '&.Mui-selected': {
      backgroundColor: '#FFF0F6 !important',
      '&:hover': {
        backgroundColor: '#FFE3EF !important'
      }
    },
    '&:hover': {
      backgroundColor: '#F8F9FA'
    }
  };

  const containerRef = React.useRef(null);
  const selectRef = React.useRef(null);
  const [localOpen, setLocalOpen] = React.useState(false);

  const handleContainerClick = (e) => {
    if (disabled) return;
    // Select 전체 영역 클릭 처리
    const clickedElement = e.target;
    
    // MenuItem이나 Menu를 클릭한 경우는 무시 (드롭다운 닫기 동작 보장)
    if (clickedElement.closest('.MuiMenu-paper') || 
        clickedElement.closest('.MuiMenuItem-root') ||
        clickedElement.closest('.MuiPopover-root')) {
      return;
    }
    
    // 아이콘 클릭인지 확인
    const isIconClick = clickedElement.tagName === 'IMG' || 
                       clickedElement.closest('img[src*="select"]') ||
                       clickedElement.closest('.MuiSelect-icon');
    
    // 드롭다운이 이미 열려있는 경우
    const isOpen = open !== undefined ? open : localOpen;
    if (isOpen) {
      // 아이콘 클릭인 경우에만 닫기 처리
      if (isIconClick) {
        if (onClose) {
          onClose();
        } else {
          setLocalOpen(false);
        }
      }
      return;
    }
    
    // Select의 input 요소 찾기
    if (containerRef.current) {
      const inputElement = containerRef.current.querySelector('input[readonly]') || 
                          containerRef.current.querySelector('input[aria-haspopup="listbox"]') ||
                          containerRef.current.querySelector('[role="combobox"]') ||
                          containerRef.current.querySelector('.MuiSelect-select');
      
      if (inputElement) {
        // input 요소를 직접 클릭한 경우는 기본 동작에 맡김
        if (clickedElement === inputElement || clickedElement.closest('input')) {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        // Select 열기
        if (onOpen) {
          onOpen();
        } else {
          setLocalOpen(true);
        }
        
        // input 요소 클릭
        setTimeout(() => {
          if (inputElement) {
            inputElement.focus();
            inputElement.click();
          }
        }, 0);
      }
    }
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: fullWidth ? '100%' : width,
        cursor: disabled ? 'default' : 'pointer',
        pointerEvents: disabled ? 'none' : 'auto',
        '&:hover': {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: hoverBorderColor
          }
        }
      }}
      onClick={handleContainerClick}
    >
      <Select
        ref={selectRef}
        value={value}
        onChange={onChange}
        size={size}
        open={open !== undefined ? open : localOpen}
        onOpen={(e) => {
          setLocalOpen(true);
          if (onOpen) onOpen(e);
        }}
        onClose={(e) => {
          setLocalOpen(false);
          if (onClose) onClose(e);
        }}
        name={name}
        required={required}
        fullWidth={fullWidth}
        IconComponent={() => (
          <Box
            component="img"
            src={selectIcon}
            sx={{
              width: 20,
              height: 20,
              ml: 1.25,
              mr: 2.5,
              pointerEvents: 'auto',
              cursor: 'pointer',
              touchAction: 'manipulation',
              position: 'relative',
              zIndex: 10,
              ...(disabled && { opacity: 0.45 })
            }}
          />
        )}
        sx={{
          ...defaultSx,
          cursor: 'pointer',
          width: '100%',
          '& .MuiOutlinedInput-root': {
            cursor: 'pointer',
            width: '100%',
            height: '100%',
            position: 'relative',
            touchAction: 'manipulation',
            pointerEvents: 'auto',
            zIndex: 1,
            '&:hover': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: hoverBorderColor
              }
            }
          },
          '& .MuiOutlinedInput-input': {
            cursor: 'pointer',
            paddingRight: '40px !important',
            width: '100%',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            pointerEvents: 'auto'
          },
          '& .MuiInputBase-input': {
            cursor: 'pointer',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            pointerEvents: 'auto'
          },
          '& .MuiSelect-select': {
            ...defaultSx['& .MuiSelect-select'],
            cursor: 'pointer',
            userSelect: 'none',
            WebkitUserSelect: 'none',
            MozUserSelect: 'none',
            msUserSelect: 'none',
            width: '100%',
            paddingRight: '40px !important',
            boxSizing: 'border-box',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
            pointerEvents: 'auto',
            minHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            zIndex: 1
          },
          '& .MuiSelect-icon': {
            cursor: 'pointer',
            pointerEvents: 'auto',
            touchAction: 'manipulation',
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2
          },
          // 아이콘 영역 클릭 가능
          '& .MuiSelect-iconOpen': {
            cursor: 'pointer',
            pointerEvents: 'auto',
            touchAction: 'manipulation',
            zIndex: 2
          },
          '& .MuiOutlinedInput-notchedOutline': {
            pointerEvents: 'none'
          },
          '&.Mui-focused': {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: focusBorderColor
            }
          },
          '& .MuiSelect-root': {
            cursor: 'pointer',
            width: '100%',
            touchAction: 'manipulation',
            pointerEvents: 'auto',
            position: 'relative',
            zIndex: 1
          }
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: resolvedMenuMaxHeight,
              overflow: 'visible',
              '& .MuiMenuItem-root': {
                minHeight: 48,
                padding: '12px 16px',
                cursor: 'pointer',
                touchAction: 'manipulation',
                '&:hover': {
                  backgroundColor: '#F8F9FA'
                },
                '&.Mui-selected': {
                  backgroundColor: '#FFF0F6',
                  '&:hover': {
                    backgroundColor: '#FFE3EF'
                  }
                }
              }
            }
          },
          MenuListProps: {
            sx: {
              maxHeight: resolvedMenuMaxHeight,
              overflow: menuMaxHeight != null ? 'visible' : 'auto',
              paddingTop: 0,
              paddingBottom: 0,
            },
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left'
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left'
          }
        }}
        disabled={disabled}
        {...props}
      >
        {options.map((option) => (
          <MenuItem 
            key={option.value} 
            value={option.value}
            sx={menuItemSx}
          >
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default CustomSelect; 